import { useState } from "react";

import { useAlertContext } from "@shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { AssignmentResponse, AssignmentStatus, assignmentsApi } from "@/api";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

export function useAssignment(assignment: AssignmentResponse) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { showSuccess } = useAlertContext();

  const [completing, setCompleting] = useState(false);
  const [reopening, setReopening] = useState(false);

  const [editing, setEditing] = useState(false);
  const isCompleted = assignment.status === AssignmentStatus.Completed;

  const { mutate: completeAssignment, isPending: isCompletingPending } = useMutation({
    mutationFn: () =>
      assignmentsApi.updateAssignmentStatus({
        assignmentId: assignment.assignmentId,
        updateAssignmentStatusRequest: { status: AssignmentStatus.Completed },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QueryKey.Assignments] });
      showSuccess(t("assignments.editDialog.successfullyClosed"));
    },
  });

  const { mutate: reopenAssignment, isPending: isReopeningPending } = useMutation({
    mutationFn: () =>
      assignmentsApi.updateAssignmentStatus({
        assignmentId: assignment.assignmentId,
        updateAssignmentStatusRequest: { status: AssignmentStatus.Open },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QueryKey.Assignments] });
      showSuccess(t("assignments.editDialog.successfullyReopened"));
    },
  });

  return {
    completeAssignment,
    completing,
    setCompleting,
    editing,
    setEditing,
    isCompleted,
    reopening,
    setReopening,
    reopenAssignment,
    isTogglingStatus: isCompletingPending || isReopeningPending,
  };
}
